'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

// TypeScript declarations for reCAPTCHA
declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      reset?: (widgetId?: string) => void;
      render?: (container: string | HTMLElement, parameters: any) => number;
    };
  }
}

// reCAPTCHA configuration
const RECAPTCHA_CONFIG = {
  SCRIPT_ID: 'recaptcha-script',
  MIN_SCORE: 0.5, // Minimum score threshold (0.0 to 1.0)
  TIMEOUT: 10000, // 10 seconds timeout for script loading
  RETRY_ATTEMPTS: 3, // Number of retry attempts for failed executions
} as const;

// Common actions used throughout the Talksie platform
export const RECAPTCHA_ACTIONS = {
  LOGIN: 'login',
  SIGNUP: 'signup',
  CONTACT: 'contact',
  NEWSLETTER: 'newsletter',
  PROFILE_UPDATE: 'profile_update',
  PASSWORD_RESET: 'password_reset',
  EMAIL_VERIFICATION: 'email_verification',
  FILE_UPLOAD: 'file_upload', // ✅ ADDED: For file upload validation
  INTERPRETER_BOOKING: 'interpreter_booking', // ✅ ADDED: For booking system
  PAYMENT_PROCESS: 'payment_process', // ✅ ADDED: For payment security
} as const;

export type RecaptchaAction = typeof RECAPTCHA_ACTIONS[keyof typeof RECAPTCHA_ACTIONS];

interface RecaptchaResult {
  token: string | null;
  score?: number;
  success: boolean;
  error?: string;
  retryCount?: number;
}

// ✅ ENHANCED: File validation integration interface
interface FileValidationOptions {
  maxSize?: number; // in MB
  allowedTypes?: string[];
  required?: boolean;
}

export const useRecaptchaVerification = () => {
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastExecutionTime, setLastExecutionTime] = useState<number>(0);
  const scriptLoadedRef = useRef(false);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);

  // Clean up function
  const cleanup = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    // Remove existing script if present
    const existingScript = document.getElementById(RECAPTCHA_CONFIG.SCRIPT_ID);
    if (existingScript) {
      existingScript.remove();
    }

    // Clean up reCAPTCHA instance in development
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.grecaptcha) {
      delete window.grecaptcha;
    }

    scriptLoadedRef.current = false;
    setIsRecaptchaReady(false);
    setIsLoading(false);
    setError(null);
    retryCountRef.current = 0;
  }, []);

  useEffect(() => {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    
    // Validate configuration
    if (!siteKey) {
      const errorMsg = 'reCAPTCHA site key not configured. Please set NEXT_PUBLIC_RECAPTCHA_SITE_KEY in your environment variables.';
      console.warn('⚠️', errorMsg);
      setError(errorMsg);
      return;
    }

    // Check if reCAPTCHA is enabled
    const isRecaptchaEnabled = process.env.NEXT_PUBLIC_RECAPTCHA_ENABLED !== 'false';
    if (!isRecaptchaEnabled) {
      console.log('ℹ️ reCAPTCHA is disabled via environment variable');
      return;
    }

    // Prevent duplicate script loading
    if (scriptLoadedRef.current || document.getElementById(RECAPTCHA_CONFIG.SCRIPT_ID)) {
      return;
    }

    const loadRecaptcha = () => {
      setIsLoading(true);
      setError(null);

      // Create script element
      const script = document.createElement('script');
      script.id = RECAPTCHA_CONFIG.SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      
      // Set up timeout for script loading
      loadTimeoutRef.current = setTimeout(() => {
        const errorMsg = `reCAPTCHA script failed to load within ${RECAPTCHA_CONFIG.TIMEOUT}ms`;
        console.error('❌', errorMsg);
        setError(errorMsg);
        setIsLoading(false);
        cleanup();
      }, RECAPTCHA_CONFIG.TIMEOUT);

      script.onload = () => {
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
          loadTimeoutRef.current = null;
        }

        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            scriptLoadedRef.current = true;
            setIsRecaptchaReady(true);
            setIsLoading(false);
            console.log('✅ reCAPTCHA ready');
          });
        } else {
          const errorMsg = 'reCAPTCHA script loaded but grecaptcha object not available';
          console.error('❌', errorMsg);
          setError(errorMsg);
          setIsLoading(false);
        }
      };

      script.onerror = (event) => {
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
          loadTimeoutRef.current = null;
        }
        
        const errorMsg = 'Failed to load reCAPTCHA script. Please check your internet connection.';
        console.error('❌', errorMsg, event);
        setError(errorMsg);
        setIsLoading(false);
      };

      // Append script to document head (more reliable than body)
      document.head.appendChild(script);
    };

    // Add a small delay to ensure DOM is fully rendered
    const timer = setTimeout(loadRecaptcha, 100);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [cleanup]);

  // ✅ ENHANCED: Execute reCAPTCHA verification with retry logic
  const executeRecaptcha = useCallback(async (
    action: RecaptchaAction | string = RECAPTCHA_ACTIONS.LOGIN,
    retryCount: number = 0
  ): Promise<RecaptchaResult> => {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    
    // Validation checks
    if (!siteKey) {
      const error = 'reCAPTCHA site key not configured';
      console.warn('⚠️', error);
      return { token: null, success: false, error };
    }

    if (!isRecaptchaReady || !window.grecaptcha) {
      const error = 'reCAPTCHA not ready. Please wait for initialization to complete.';
      console.warn('⚠️', error);
      return { token: null, success: false, error };
    }

    // ✅ ENHANCED: Rate limiting to prevent abuse
    const now = Date.now();
    if (now - lastExecutionTime < 1000) { // 1 second minimum between executions
      const error = 'reCAPTCHA rate limit exceeded. Please wait before trying again.';
      console.warn('⚠️', error);
      return { token: null, success: false, error };
    }

    try {
      console.log(`🔐 Executing reCAPTCHA for action: ${action}${retryCount > 0 ? ` (retry ${retryCount})` : ''}`);
      
      const token = await new Promise<string>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('reCAPTCHA execution timeout'));
        }, 10000); // 10 second timeout

        window.grecaptcha!.ready(() => {
          window.grecaptcha!.execute(siteKey, { action })
            .then((token) => {
              clearTimeout(timeout);
              resolve(token);
            })
            .catch((error) => {
              clearTimeout(timeout);
              reject(error);
            });
        });
      });

      if (!token) {
        throw new Error('No token returned from reCAPTCHA');
      }

      setLastExecutionTime(now);
      retryCountRef.current = 0;
      console.log('✅ reCAPTCHA token generated successfully');
      
      return { 
        token, 
        success: true,
        retryCount 
      };

    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown reCAPTCHA error';
      console.error('❌ reCAPTCHA execution error:', errorMessage);
      
      // ✅ ENHANCED: Retry logic for transient failures
      if (retryCount < RECAPTCHA_CONFIG.RETRY_ATTEMPTS && 
          (errorMessage.includes('timeout') || errorMessage.includes('network'))) {
        console.log(`🔄 Retrying reCAPTCHA execution (${retryCount + 1}/${RECAPTCHA_CONFIG.RETRY_ATTEMPTS})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return executeRecaptcha(action, retryCount + 1);
      }
      
      return { 
        token: null, 
        success: false, 
        error: errorMessage,
        retryCount
      };
    }
  }, [isRecaptchaReady, lastExecutionTime]);

  // ✅ ENHANCED: File validation with reCAPTCHA integration
  const executeRecaptchaWithFileValidation = useCallback(async (
    action: RecaptchaAction | string,
    file?: File | FileList | null,
    options?: FileValidationOptions
  ): Promise<RecaptchaResult> => {
    try {
      // File validation if provided
      if (file) {
        const fileToValidate = file instanceof FileList ? file[0] : file;
        
        if (fileToValidate) {
          const maxSize = (options?.maxSize || 5) * 1024 * 1024; // Default 5MB
          const allowedTypes = options?.allowedTypes || ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

          // File size validation
          if (fileToValidate.size > maxSize) {
            const fileSizeMB = (fileToValidate.size / 1024 / 1024).toFixed(2);
            const maxSizeMB = (maxSize / 1024 / 1024).toFixed(0);
            const error = `⚠️ File too large for interpreter profile: ${fileSizeMB}MB. Please use an image under ${maxSizeMB}MB.`;
            return { token: null, success: false, error };
          }

          // File type validation
          if (!allowedTypes.includes(fileToValidate.type)) {
            const error = `⚠️ Invalid file type. Please upload: ${allowedTypes.join(', ')}`;
            return { token: null, success: false, error };
          }

          console.log(`✅ File validation passed: ${fileToValidate.name} (${(fileToValidate.size / 1024 / 1024).toFixed(2)}MB)`);
        }
      }

      // Execute reCAPTCHA after file validation
      return await executeRecaptcha(action);
    } catch (error: any) {
      return { 
        token: null, 
        success: false, 
        error: error.message || 'File validation failed' 
      };
    }
  }, [executeRecaptcha]);

  // Convenience methods for common Talksie actions
  const verifyLogin = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.LOGIN), [executeRecaptcha]);
  const verifySignup = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.SIGNUP), [executeRecaptcha]);
  const verifyContact = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.CONTACT), [executeRecaptcha]);
  const verifyNewsletter = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.NEWSLETTER), [executeRecaptcha]);
  const verifyProfileUpdate = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.PROFILE_UPDATE), [executeRecaptcha]);
  const verifyPasswordReset = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.PASSWORD_RESET), [executeRecaptcha]);
  
  // ✅ ENHANCED: New convenience methods for Talksie marketplace
  const verifyFileUpload = useCallback((file?: File | FileList | null, options?: FileValidationOptions) => 
    executeRecaptchaWithFileValidation(RECAPTCHA_ACTIONS.FILE_UPLOAD, file, options), [executeRecaptchaWithFileValidation]);
  
  const verifyInterpreterBooking = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.INTERPRETER_BOOKING), [executeRecaptcha]);
  const verifyPaymentProcess = useCallback(() => executeRecaptcha(RECAPTCHA_ACTIONS.PAYMENT_PROCESS), [executeRecaptcha]);

  // ✅ ENHANCED: Advanced signup verification with file validation
  const verifySignupWithFile = useCallback((file?: File | FileList | null) => 
    executeRecaptchaWithFileValidation(RECAPTCHA_ACTIONS.SIGNUP, file, {
      maxSize: 5, // 5MB for interpreter profiles
      allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      required: true
    }), [executeRecaptchaWithFileValidation]);

  // Reset reCAPTCHA (useful for forms that can be submitted multiple times)
  const resetRecaptcha = useCallback(() => {
    if (window.grecaptcha?.reset) {
      window.grecaptcha.reset();
      console.log('🔄 reCAPTCHA reset');
    }
    retryCountRef.current = 0;
    setError(null);
  }, []);

  // ✅ ENHANCED: Health check function
  const healthCheck = useCallback(async (): Promise<boolean> => {
    if (!isRecaptchaReady) return false;
    
    try {
      const result = await executeRecaptcha('health_check');
      return result.success;
    } catch {
      return false;
    }
  }, [executeRecaptcha, isRecaptchaReady]);

  // Check if reCAPTCHA is enabled in the environment
  const isRecaptchaEnabled = process.env.NEXT_PUBLIC_RECAPTCHA_ENABLED !== 'false';

  return {
    // Core functionality
    executeRecaptcha,
    executeRecaptchaWithFileValidation,
    isRecaptchaReady,
    isLoading,
    error,
    
    // Standard convenience methods
    verifyLogin,
    verifySignup,
    verifyContact,
    verifyNewsletter,
    verifyProfileUpdate,
    verifyPasswordReset,
    
    // ✅ ENHANCED: Talksie-specific convenience methods
    verifyFileUpload,
    verifyInterpreterBooking,
    verifyPaymentProcess,
    verifySignupWithFile,
    
    // Utility methods
    resetRecaptcha,
    healthCheck,
    isRecaptchaEnabled,
    
    // ✅ ENHANCED: Status information
    lastExecutionTime,
    retryCount: retryCountRef.current,
    
    // Configuration info
    siteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
    config: RECAPTCHA_CONFIG,
  };
};

// ✅ ENHANCED: Helper function to validate reCAPTCHA score (for server-side use)
export const isValidRecaptchaScore = (score: number): boolean => {
  return score >= RECAPTCHA_CONFIG.MIN_SCORE;
};

// ✅ ENHANCED: Helper function for file size validation (client-side)
export const validateFileForRecaptcha = (
  file: File, 
  options: FileValidationOptions = {}
): { valid: boolean; error?: string } => {
  const maxSize = (options.maxSize || 5) * 1024 * 1024; // Default 5MB
  const allowedTypes = options.allowedTypes || ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  if (file.size > maxSize) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
    const maxSizeMB = (maxSize / 1024 / 1024).toFixed(0);
    return {
      valid: false,
      error: `File too large: ${fileSizeMB}MB. Maximum size: ${maxSizeMB}MB`
    };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${allowedTypes.join(', ')}`
    };
  }

  return { valid: true };
};

// Export configuration and types for use in other parts of the app
export { RECAPTCHA_CONFIG };
export type { RecaptchaResult, FileValidationOptions };
